import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as TemplateSvg } from '../../../../../assets/img/Templates.svg';
import { ReactComponent as CustomSvg } from '../../../../../../src/assets/img/Custom.svg';
import { CellEditor, sanitizeHtmlForSave } from './cell-editor';
import { IconButton, Button } from '@material-ui/core';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PreviewIcon from '../../../../../assets/img/PreviewIcon.svg';
import TemplateCanvas from './TemplateCanvas';
import TemplateRightPanel from './TemplateRightPanel';
import Modal from '@material-ui/core/Modal';
import { makeStyles } from '@material-ui/core/styles';
import { getDefaultEmailTemplate } from '../../../../../utils/cmsUtils';
import TemplatePreview from './TemplatePreview';
const useChooserStyles = makeStyles(() => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, minmax(160px, 1fr))',
    gap: 28,
    justifyContent: 'center',
    alignItems: 'center',
  },
  btn: {
    appearance: 'none',
    border: 0,
    background: 'transparent',
    padding: 0,
    margin: 0,
    cursor: 'pointer',
    outline: 'none',
    width: 160,
    height: 'auto',
  },
  svg: {
    width: 160,
    height: 'auto',
    display: 'block',
    userSelect: 'none',
    pointerEvents: 'none',
  },
}));

const setSvgHoverState = (svgRoot, hoverOn) => {
  if (!svgRoot) return;
  const FILL = '#CFF8DF';

  try {
    let target = null;
    let maxArea = 0;

    svgRoot.querySelectorAll('rect, path, polygon, circle, ellipse').forEach((el) => {
      if (typeof el.getBBox !== 'function') return;
      const box = el.getBBox();
      const area = box.width * box.height;
      if (area > maxArea) {
        maxArea = area;
        target = el;
      }
    });

    if (!target) return;

    if (hoverOn) {
      if (target.dataset.prevFill === undefined) {
        target.dataset.prevFill = target.getAttribute('fill') ?? '';
      }
      target.setAttribute('fill', FILL);
    } else {
      const prev = target.dataset.prevFill;
      if (prev !== undefined) {
        if (prev === '') target.removeAttribute('fill');
        else target.setAttribute('fill', prev);
      } else {
        target.setAttribute('fill', 'none');
      }
    }
  } catch {}
};

const SvgChoiceButton = ({ Svg, label, onClick }) => {
  const classes = useChooserStyles();
  const ref = useRef(null);

  return (
    <button
      type='button'
      className={classes.btn}
      aria-label={label}
      onClick={onClick}
      onMouseEnter={() => setSvgHoverState(ref.current, true)}
      onFocus={() => setSvgHoverState(ref.current, true)}
      onMouseLeave={() => setSvgHoverState(ref.current, false)}
      onBlur={() => setSvgHoverState(ref.current, false)}
    >
      <Svg ref={ref} className={classes.svg} />
    </button>
  );
};

const isCanvasEmpty = (rawHtml) => {
  const s = (rawHtml || '').trim();
  if (!s) return true;
  const cleaned = s
    .replace(/<p><br><\/p>/gi, '')
    .replace(/<p>\s*<\/p>/gi, '')
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '')
    .trim();
  if (!cleaned) return true;
  try {
    const doc = new DOMParser().parseFromString(s, 'text/html');
    const body = doc.body;
    body.querySelectorAll('p').forEach((p) => {
      if (p.textContent.trim() === '' && p.children.length === 0) p.remove();
    });
    const hasBlocks =
      body.textContent.trim() !== '' || body.querySelector('img, video, table, [data-comp], a');
    return !hasBlocks;
  } catch {
    return false;
  }
};

const EmailServiceTemplateTab = ({
  formData,
  handleInputChange,
  onNext,
  onSave,
  onStageChange,
  onValidationChange,
  registerHistoryApi,
  isViewOnly = false,
  isEditing = false,
}) => {
  const current = formData.templateMode.value;
  const hasExistingContent = !!formData?.htmlTemplateBody?.value;
  const [stage, setStage] = useState(() => {
    if (isEditing && hasExistingContent && current === 'Template') return 'edit';
    return 'pick';
  });
  const [picked, setPicked] = useState(null);
  const [initializedFromExisting, setInitializedFromExisting] = useState(hasExistingContent);
  const extractHtmlAndBgColor = (savedHtml) => {
    if (!savedHtml) return { html: '', bgColor: '#ffffff' };
    const bgColorMatch = savedHtml.match(/background-color:\s*([^;}"']+)/i);
    if (bgColorMatch) {
      const extractedBgColor = bgColorMatch[1].trim();
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = savedHtml;
      const innerContent = savedHtml;
      return { html: innerContent, bgColor: extractedBgColor };
    }
    return { html: savedHtml, bgColor: '#ffffff' };
  };
  const { html: initialHtml, bgColor: initialBgColor } = extractHtmlAndBgColor(
    formData?.htmlTemplateBody?.value,
  );
  const [html, setHtml] = useState(initialHtml);
  const [tableHtml, setTableHtml] = useState('');
  const [bgColor, setBgColor] = useState(initialBgColor);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isTemplateInvalid, setIsTemplateInvalid] = useState(() => {
    if (!initialHtml) return false;
    const s = initialHtml.trim();
    if (!s) return false;
    return (
      s.includes('Double Click to Enter Text...') ||
      s.includes('Image placeholder') ||
      s.includes('Add social links from the panel')
    );
  });
  const [isTemplateEmpty, setIsTemplateEmpty] = useState(() => isCanvasEmpty(initialHtml));
  const askConfirm = () => setConfirmOpen(true);
  const closeConfirm = () => setConfirmOpen(false);
  const openPreview = () => setPreviewOpen(true);
  const closePreview = () => setPreviewOpen(false);
  const discardAndExit = () => {
    setConfirmOpen(false);
    setPicked(null);
    setStage('pick');
    setHtml('');
    setBgColor('#ffffff');
    setType('');
  };
  const bodyHtml = useMemo(() => {
    const raw = html || '';
    try {
      if (typeof window !== 'undefined' && raw.includes('<body')) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(raw, 'text/html');
        let bodyContent = (doc?.body?.innerHTML || '')?.trim();
        bodyContent = bodyContent
          ?.replace(/>\s+</g, '><') 
          ?.replace(/^\s+/, '') 
          ?.replace(/\s+$/, '');
        const bgMatch = bodyContent.match(/background-color:\s*([^;}"']+)/i);
        if (bgMatch) {
          setBgColor(bgMatch[1].trim());
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = bodyContent;
          return bodyContent;
        }
        return bodyContent;
      }
    } catch {}
    return raw.trim();
  }, [html]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (initializedFromExisting && !picked?.templateType) {
        return;
      }
      if (!picked?.templateType) {
        if (current === 'Template' && stage === 'pick' && !initialHtml) {
          setHtml('');
          return;
        }
        if (initialHtml && !html) {
          setHtml(initialHtml);
        }
        return;
      }
      setLoading(true);
      try {
        const res = await getDefaultEmailTemplate(picked.templateType);
        const value = res?.data?.getDefaultTemplate?.value ?? '';
        if (!cancelled) {
          setHtml(value);
          setInitializedFromExisting(false);
        }
      } catch (e) {
        if (!cancelled) {
          setHtml(
            '<html><body style="padding:24px;color:#dc2626">Failed to load template.</body></html>',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [picked]);
  useEffect(() => {
    onStageChange && onStageChange(stage);
  }, [stage, onStageChange]);

  useEffect(() => {
    onValidationChange && onValidationChange(!isTemplateInvalid && !isTemplateEmpty);
  }, [isTemplateInvalid, isTemplateEmpty, onValidationChange]);
  useEffect(() => {
    if (
      !isViewOnly &&
      isEditing &&
      hasExistingContent &&
      current === 'Template' &&
      stage === 'pick'
    ) {
      setStage('edit');
    }
  }, [isViewOnly, isEditing, hasExistingContent, current, stage]);

  const classes = useChooserStyles();

  const setType = (val) =>
    handleInputChange({
      templateMode: { ...formData.templateMode, value: val, errors: {} },
    });
  if (isViewOnly) {
    const templateData = {
      name: formData.name?.value || '',
      fromName: formData.fromName?.value || '',
      fromAddress: formData.fromAddress?.value || '',
      subject: formData.subject?.value || '',
      htmlTemplateBody: formData.htmlTemplateBody?.value || '',
    };

    return (
      <div className='w-100'>
        <div className='template-preview-container'>
          <div className='row mt-4 g-0'>
            <div className='col-lg-8 col-md-12 mb-4'>
              <div className='preview-section'>
                <TemplatePreview template={templateData} viewType='desktop' />
              </div>
            </div>
            <div className='col-lg-4 col-md-12 mb-4'>
              <div className='preview-section'>
                <TemplatePreview template={templateData} viewType='mobile' />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const renderRootChooser = () => {
    return (
      <div
        style={{
          border: '1px solid #E6E6E6',
          borderRadius: 8,
          height: '80vh',
          padding: 24,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#fff',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#9aa3a7', fontSize: 12, marginBottom: 16 }}>Select an option</div>
          <div className={classes.grid}>
            <SvgChoiceButton
              Svg={TemplateSvg}
              label='Templates'
              onClick={() => {
                setType('Template');
                setStage('pick');
              }}
            />
            <SvgChoiceButton
              Svg={CustomSvg}
              label='Custom'
              onClick={() => {
                setPicked(null);
                setStage('pick');
                setHtml('');
                setBgColor('#ffffff');

                setType('Custom');
              }}
            />
          </div>
        </div>
      </div>
    );
  };

  function getModalStyle() {
    const top = 50;
    const left = 50;
    return { top: `${top}%`, left: `${left}%`, transform: `translate(-${top}%, -${left}%)` };
  }

  const useConfirmStyles = makeStyles((theme) => ({
    paper: {
      position: 'absolute',
      width: 420,
      backgroundColor: theme.palette.background.paper,
      border: 'none',
      borderRadius: '10px',
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2, 4, 3),
    },
  }));

  const ConfirmPopup = ({
    open,
    title,
    description,
    onCancel,
    onConfirm,
    image,
    confirmLabel = 'Discard',
    cancelLabel = 'Cancel',
  }) => {
    const classesConfirm = useConfirmStyles();
    const [modalStyle] = React.useState(getModalStyle);

    return (
      <Modal open={open} onClose={onCancel}>
        <div style={modalStyle} className={classesConfirm.paper}>
          <div className='delete-popup'>
            {image && (
              <div className='row d-flex justify-content-center my-3'>
                <img alt='confirm' src={image} className='img' />
              </div>
            )}
            <div className='delete-popup-title'>{title}</div>
            {description && <p className='delete-popup-description'>{description}</p>}
            <div className='col-md-10 offset-1 d-flex justify-content-around px-4 mt-4'>
              <Button variant='outlined' size='large' color='secondary' onClick={onCancel}>
                {cancelLabel}
              </Button>
              <Button variant='contained' size='large' color='primary' onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    );
  };

  if (current === 'Custom') {
    const goBack = () => {
      if (isCanvasEmpty(html)) {
        discardAndExit();
      } else {
        askConfirm();
      }
    };

    return (
      <div className='w-100'>
        <div className='tpl-back-row'>
          <IconButton aria-label='Back' onClick={goBack} size='small' className='tpl-back-btn'>
            <ArrowBackIcon className='tpl-back-ic' />
          </IconButton>
          <button type='button' className='tpl-back-text' onClick={goBack}>
            Back
          </button>
        </div>

        <CellEditor
          value={{ html: html || '', bgColor: bgColor }}
          onChange={(payload) => {
            const newHtml = payload?.html || '';
            const newTableHtml = payload?.tableHtml || '';
            const newBgColor = payload?.bgColor || '#ffffff';
            const isValid = payload?.isValid !== false;
            const isEmptyFlag = payload?.isEmpty === true;
            setIsTemplateInvalid(!isValid);
            setIsTemplateEmpty(isEmptyFlag);
            setHtml(newHtml);
            setTableHtml(newTableHtml);
            setBgColor(newBgColor);
            const cleanHtml = sanitizeHtmlForSave(newHtml);
            handleInputChange?.({
              htmlTemplateBody: {
                ...(formData?.htmlTemplateBody || {
                  fieldName: 'HTML Template Body',
                  validations: [],
                  errors: {},
                  tab: 1,
                }),
                value: newHtml,
                errors: {},
              },
            });
          }}
          formData={formData}
          handleInputChange={handleInputChange}
          isReadOnly={false}
          registerHistoryApi={registerHistoryApi}
          canvasFooter={
            <>
              <IconButton className='preview-icon-btn' onClick={openPreview}>
                <img src={PreviewIcon} alt='Preview' className='preview-icon' />
              </IconButton>

              <Button
                variant='contained'
                color='primary'
                style={{ padding: '0px 35px' }}
                disabled={isTemplateInvalid || isTemplateEmpty}
                onClick={() => {
                  const cleanHtml = sanitizeHtmlForSave(html || '');
                  handleInputChange?.({
                    htmlTemplateBody: {
                      ...(formData?.htmlTemplateBody || {
                        fieldName: 'HTML Template Body',
                        validations: [],
                        errors: {},
                        tab: 1,
                      }),
                      value: html || '',
                      errors: {},
                    },
                  });
                  if (isEditing) {
                    onSave?.();
                  } else {
                    onNext?.();
                  }
                }}
              >
                {isEditing ? 'Save' : 'Next'}
              </Button>
            </>
          }
        />
        <ConfirmPopup
          open={confirmOpen}
          title='You have unsaved changes in this template'
          description='Leaving now will discard all changes made to this template.'
          onCancel={closeConfirm}
          onConfirm={discardAndExit}
          confirmLabel='Discard'
          cancelLabel='Cancel'
        />
        <Modal open={previewOpen} onClose={closePreview}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90vw',
              maxWidth: '1400px',
              maxHeight: '90vh',
              backgroundColor: '#fff',
              borderRadius: '10px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              padding: '24px',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Template Preview</h5>
              <IconButton onClick={closePreview} size='small'>
                <span style={{ fontSize: '20px' }}>×</span>
              </IconButton>
            </div>
            <div className='template-preview-container'>
              <div className='row mt-4 g-0'>
                <div className='col-lg-8 col-md-12 mb-4'>
                  <div className='preview-section'>
                    <TemplatePreview
                      template={{
                        name: formData.name?.value || 'Email Template Preview',
                        fromName: formData.fromName?.value || 'Hotel',
                        fromAddress: formData.fromAddress?.value || 'noreply@hotel.com',
                        subject: formData.subject?.value || 'Email Subject',
                        htmlTemplateBody: html || '',
                      }}
                      viewType='desktop'
                    />
                  </div>
                </div>
                <div className='col-lg-4 col-md-12 mb-4'>
                  <div className='preview-section'>
                    <TemplatePreview
                      template={{
                        name: formData.name?.value || 'Email Template Preview',
                        fromName: formData.fromName?.value || 'Hotel',
                        fromAddress: formData.fromAddress?.value || 'noreply@hotel.com',
                        subject: formData.subject?.value || 'Email Subject',
                        htmlTemplateBody: html || '',
                      }}
                      viewType='mobile'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  if (current === 'Template') {
    const handleBack = () => {
      const candidate = stage === 'pick' ? html : bodyHtml || html;
      if (isCanvasEmpty(candidate)) {
        discardAndExit();
      } else {
        askConfirm();
      }
    };
    const BackBar = (
      <div className='tpl-back-row'>
        <IconButton aria-label='Back' onClick={handleBack} size='small' className='tpl-back-btn'>
          <ArrowBackIcon className='tpl-back-ic' />
        </IconButton>
        <button type='button' className='tpl-back-text' onClick={handleBack}>
          Back
        </button>
      </div>
    );

    function getModalStyle() {
      const top = 50;
      const left = 50;
      return { top: `${top}%`, left: `${left}%`, transform: `translate(-${top}%, -${left}%)` };
    }

    const useConfirmStyles = makeStyles((theme) => ({
      paper: {
        position: 'absolute',
        width: 420,
        backgroundColor: theme.palette.background.paper,
        border: 'none',
        borderRadius: '10px',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(2, 4, 3),
      },
    }));

    const ConfirmPopup = ({
      open,
      title,
      description,
      onCancel,
      onConfirm,
      image,
      confirmLabel = 'Discard',
      cancelLabel = 'Cancel',
    }) => {
      const classesConfirm = useConfirmStyles();
      const [modalStyle] = React.useState(getModalStyle);

      return (
        <Modal open={open} onClose={onCancel}>
          <div style={modalStyle} className={classesConfirm.paper}>
            <div className='delete-popup'>
              {image && (
                <div className='row d-flex justify-content-center my-3'>
                  <img alt='confirm' src={image} className='img' />
                </div>
              )}
              <div className='delete-popup-title'>{title}</div>
              {description && <p className='delete-popup-description'>{description}</p>}
              <div className='col-md-10 offset-1 d-flex justify-content-around px-4 mt-4'>
                <Button variant='outlined' size='large' color='secondary' onClick={onCancel}>
                  {cancelLabel}
                </Button>
                <Button variant='contained' size='large' color='primary' onClick={onConfirm}>
                  {confirmLabel}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      );
    };

    if (stage === 'pick') {
      const canChoose = !!picked && !!bodyHtml;
      return (
        <div className='w-100'>
          {BackBar}
          <div className='email-template d-flex' style={{ margin: '0px -30px 0px 0px ' }}>
            <div className='flex-grow-1 me-3'>
              {loading ? (
                <div style={{ padding: 24, color: '#9aa3a7' }}>Loading template…</div>
              ) : (
                <>
                  <TemplateCanvas
                    key={`tpl-canvas-${picked?.id || 'none'}-${stage}`}
                    html={html}
                    onChange={() => {}}
                    isReadOnly
                  />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                    <Button
                      variant='contained'
                      color='primary'
                      disabled={!canChoose}
                      onClick={() => setStage('edit')}
                    >
                      Choose Template
                    </Button>
                  </div>
                </>
              )}
            </div>

            <TemplateRightPanel
              selectedId={picked?.id}
              onPick={(t) => setPicked(t)}
              onClose={handleBack}
            />
          </div>
          <ConfirmPopup
            open={confirmOpen}
            title='You have unsaved changes in this template'
            description='Leaving now will discard all changes made to this template.'
            onCancel={closeConfirm}
            onConfirm={discardAndExit}
            confirmLabel='Discard'
            cancelLabel='Cancel'
          />
          <Modal open={previewOpen} onClose={closePreview}>
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '90vw',
                maxWidth: '1400px',
                maxHeight: '90vh',
                backgroundColor: '#fff',
                borderRadius: '10px',
                boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                padding: '24px',
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Template Preview</h5>
                <IconButton onClick={closePreview} size='small'>
                  <span style={{ fontSize: '20px' }}>×</span>
                </IconButton>
              </div>
              <div className='template-preview-container'>
                <div className='row mt-4 g-0'>
                  <div className='col-lg-8 col-md-12 mb-4'>
                    <div className='preview-section'>
                      <TemplatePreview
                        template={{
                          name: formData.name?.value || 'Email Template Preview',
                          fromName: formData.fromName?.value || 'Hotel',
                          fromAddress: formData.fromAddress?.value || 'noreply@hotel.com',
                          subject: formData.subject?.value || 'Email Subject',
                          htmlTemplateBody: bodyHtml || html || '',
                        }}
                        viewType='desktop'
                      />
                    </div>
                  </div>
                  <div className='col-lg-4 col-md-12 mb-4'>
                    <div className='preview-section'>
                      <TemplatePreview
                        template={{
                          name: formData.name?.value || 'Email Template Preview',
                          fromName: formData.fromName?.value || 'Hotel',
                          fromAddress: formData.fromAddress?.value || 'noreply@hotel.com',
                          subject: formData.subject?.value || 'Email Subject',
                          htmlTemplateBody: bodyHtml || html || '',
                        }}
                        viewType='mobile'
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Modal>
        </div>
      );
    }

    return (
      <div className='w-100'>
        {BackBar}

        <CellEditor
          value={{ html: bodyHtml, bgColor: bgColor }}
          onChange={(payload) => {
            const newHtml = payload?.html || '';
            const newTableHtml = payload?.tableHtml || '';
            const newBgColor = payload?.bgColor || '#ffffff';
            const isValid = payload?.isValid !== false;
            const isEmptyFlag = payload?.isEmpty === true;
            setIsTemplateInvalid(!isValid);
            setIsTemplateEmpty(isEmptyFlag);
            setHtml(newHtml);
            setTableHtml(newTableHtml);
            setBgColor(newBgColor);
            handleInputChange?.({
              htmlTemplateBody: {
                ...(formData?.htmlTemplateBody || {
                  fieldName: 'HTML Template Body',
                  validations: [],
                  errors: {},
                  tab: 1,
                }),
                value: newHtml,
                errors: {},
              },
            });
          }}
          formData={formData}
          handleInputChange={handleInputChange}
          isReadOnly={false}
          registerHistoryApi={registerHistoryApi}
          canvasFooter={
            <>
              <IconButton onClick={openPreview} className='preview-icon-btn'>
                <img src={PreviewIcon} alt='Preview' className='preview-icon' />
              </IconButton>
              <Button
                variant='contained'
                color='primary'
                disabled={isTemplateInvalid || isTemplateEmpty}
                onClick={() => {
                  const finalHtml = bodyHtml || html;
                  handleInputChange?.({
                    htmlTemplateBody: {
                      ...(formData?.htmlTemplateBody || {
                        fieldName: 'HTML Template Body',
                        validations: [],
                        errors: {},
                        tab: 1,
                      }),
                      value: finalHtml,
                      errors: {},
                    },
                  });
                  if (isEditing) {
                    onSave?.();
                  } else {
                    onNext?.();
                  }
                }}
              >
                {isEditing ? 'Save' : 'Next'}
              </Button>
            </>
          }
        />

        <ConfirmPopup
          open={confirmOpen}
          title='You have unsaved changes in this template'
          description='Leaving now will discard all changes made to this template.'
          onCancel={closeConfirm}
          onConfirm={discardAndExit}
          confirmLabel='Discard'
          cancelLabel='Cancel'
        />
        <Modal open={previewOpen} onClose={closePreview}>
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '90vw',
              maxWidth: '1400px',
              maxHeight: '90vh',
              backgroundColor: '#fff',
              borderRadius: '10px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              padding: '24px',
              overflow: 'auto',
            }}
          >
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <h5 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Template Preview</h5>
              <IconButton onClick={closePreview} size='small'>
                <span style={{ fontSize: '20px' }}>×</span>
              </IconButton>
            </div>
            <div className='template-preview-container'>
              <div className='row mt-4 g-0'>
                <div className='col-lg-8 col-md-12 mb-4'>
                  <div className='preview-section'>
                    <TemplatePreview
                      template={{
                        name: formData.name?.value || 'Email Template Preview',
                        fromName: formData.fromName?.value || 'Hotel',
                        fromAddress: formData.fromAddress?.value || 'noreply@hotel.com',
                        subject: formData.subject?.value || 'Email Subject',
                        htmlTemplateBody: bodyHtml || html || '',
                      }}
                      viewType='desktop'
                    />
                  </div>
                </div>
                <div className='col-lg-4 col-md-12 mb-4'>
                  <div className='preview-section'>
                    <TemplatePreview
                      template={{
                        name: formData.name?.value || 'Email Template Preview',
                        fromName: formData.fromName?.value || 'Hotel',
                        fromAddress: formData.fromAddress?.value || 'noreply@hotel.com',
                        subject: formData.subject?.value || 'Email Subject',
                        htmlTemplateBody: bodyHtml || html || '',
                      }}
                      viewType='mobile'
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Modal>
      </div>
    );
  }

  return <div className='w-100'>{renderRootChooser()}</div>;
};

export default EmailServiceTemplateTab;
